import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isTrustedShellOrigin } from '../constants/origins';

/**
 * Phase 1 URL sync bridge — sub-app side.
 *
 * Keeps the biztada-app shell URL hash in sync with this sub-app's React Router
 * location so the current sub-page is always visible in the browser address bar,
 * shareable as a deep-link, and preserved across hard refreshes.
 *
 * Protocol:
 *   OUT  { type: 'IFRAME_NAVIGATE', path: string }  — sent on every route change
 *                                                      and in response to REQUEST_PATH
 *   IN   { type: 'BIZTADA_NAVIGATE', path: string } — navigate to path
 *                                                      (deep-link restore on refresh)
 *   IN   { type: 'BIZTADA_REQUEST_PATH' }           — respond with current path
 *                                                      (shell requests path on tab switch)
 *
 * Must be mounted INSIDE <Router> so useLocation / useNavigate are available.
 * Renders nothing — call as <IframeNavSync /> using the companion component below,
 * or call this hook directly in a component that lives inside the Router tree.
 */
export function useIframeNavigationSync(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const isInIframe = window.self !== window.top;

  // 1. Notify parent shell of every navigation change
  useEffect(() => {
    if (!isInIframe) return;
    const path = location.pathname + location.search + (location.hash || '');
    console.log('[IframeNavSync] → IFRAME_NAVIGATE', path);
    window.parent.postMessage({ type: 'IFRAME_NAVIGATE', path }, '*');
  }, [location, isInIframe]);

  // 2. Handle inbound messages from shell
  useEffect(() => {
    if (!isInIframe) return;

    const handler = (e: MessageEvent) => {
      if (!isTrustedShellOrigin(e.origin)) return;

      // Shell asks us to navigate to a restored path (deep-link / F5)
      if (e.data?.type === 'BIZTADA_NAVIGATE') {
        const path = e.data.path as string;
        if (typeof path === 'string' && path.startsWith('/')) {
          navigate(path, { replace: true });
        }
        return;
      }

      // Shell switched back to this tab — reply with current path
      if (e.data?.type === 'BIZTADA_REQUEST_PATH') {
        const path = location.pathname + location.search + (location.hash || '');
        window.parent.postMessage({ type: 'IFRAME_NAVIGATE', path }, '*');
        return;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  // location in deps so BIZTADA_REQUEST_PATH always captures the latest path
  }, [navigate, location, isInIframe]);
}
